import type { AlgoliaQueryState } from '../../templates/components/collections/searchStateUrlStorageHelpers';

const sanitizeTokenValue = (value: string, shouldPreserveAllCharacters = false): string => {
  const trimmed = value.trim();
  if (shouldPreserveAllCharacters) {
    return /[a-z0-9]/i.test(trimmed) ? trimmed : '';
  }
  return trimmed.replace(/[^a-z0-9]/gi, '');
};

const extractAndSanitizeFilterKey = (key: string): string => sanitizeTokenValue(key.replace(/^facets\./, ''));

const sanitizeFilterValues = (value: unknown, shouldPreserveSpecialCharacters = false): Array<string> => {
  if (Array.isArray(value)) {
    return value
      .filter(item => item !== undefined && item !== null && String(item).trim() !== '')
      .map(item => sanitizeTokenValue(String(item), shouldPreserveSpecialCharacters))
      .filter(Boolean);
  }

  if (value === undefined || value === null || value === '') {
    return [];
  }

  const sanitizedValue = sanitizeTokenValue(String(value), shouldPreserveSpecialCharacters);

  return sanitizedValue ? [sanitizedValue] : [];
};

const buildFilterTuple = (name: string, operator: string, values: Array<string>): string =>
  `(${name},${operator},${values.join(',')})`;

export const buildCriteoFiltersFromSearchState = (
  searchState?: Pick<AlgoliaQueryState, 'refinementList' | 'range' | 'toggle'>
): string | undefined => {
  const filterTuples: Array<string> = [];

  Object.entries(searchState?.refinementList ?? {}).forEach(([key, value]) => {
    const extractedFilterKey = extractAndSanitizeFilterKey(key);
    const shouldPreserveSpecialCharacters = extractedFilterKey.toLowerCase() === 'brand';
    const values = sanitizeFilterValues(value, shouldPreserveSpecialCharacters);

    if (!extractedFilterKey || !values.length) {
      return;
    }

    filterTuples.push(buildFilterTuple(extractedFilterKey, values.length > 1 ? 'in' : 'eq', values));
  });

  const priceRange = searchState?.range?.['facets.Price'];

  if (priceRange) {
    const [minPrice = '', maxPrice = ''] = String(priceRange).split(':');
    const extractedPriceKey = extractAndSanitizeFilterKey('facets.Price');
    const sanitizedMinPrice = sanitizeTokenValue(minPrice);
    const sanitizedMaxPrice = sanitizeTokenValue(maxPrice);

    if (extractedPriceKey && sanitizedMinPrice) {
      filterTuples.push(buildFilterTuple(extractedPriceKey, 'ge', [sanitizedMinPrice]));
    }

    if (extractedPriceKey && sanitizedMaxPrice) {
      filterTuples.push(buildFilterTuple(extractedPriceKey, 'le', [sanitizedMaxPrice]));
    }
  }

  if (searchState?.toggle?.onPromotion) {
    filterTuples.push(buildFilterTuple(extractAndSanitizeFilterKey('onPromotion'), 'eq', ['true']));
  }

  if (searchState?.toggle?.['banner_tags.label']) {
    filterTuples.push(buildFilterTuple(extractAndSanitizeFilterKey('banner_tags.label'), 'eq', ['Clearance']));
  }

  if (searchState?.toggle?.isMarketplace) {
    filterTuples.push(buildFilterTuple(extractAndSanitizeFilterKey('isMarketplace'), 'eq', ['false']));
  }

  return filterTuples.length ? filterTuples.join(',') : undefined;
};
